import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, H2, Input, Paragraph, SizableText, YStack } from 'tamagui';
import { supabase } from '../lib/supabase';
import { useAuthStore } from '../lib/auth-store';
import { normalizeEmail, mapAuthError } from '../lib/auth-helpers';
import { Logo } from '../components/Logo';

const CODE_LENGTH = 6;
const RESEND_COOLDOWN_SECONDS = 30;

type Step = 'email' | 'code';
type Status = 'idle' | 'sending' | 'verifying';

export function Login() {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<string | null>(null);
  const [resendIn, setResendIn] = useState(0);
  // Guards against the auto-submit firing twice for the same 6 digits.
  const submittingRef = useRef(false);
  const session = useAuthStore((s) => s.session);

  // Navigate home only once the session actually lands in the store (set by
  // onAuthStateChange after verifyOtp). Driving the redirect off session — not
  // off the verify call — avoids a race where RequireAuth reads a still-null
  // session and bounces back to /login. Also forwards an already-authed visitor.
  useEffect(() => {
    if (session) navigate('/', { replace: true });
  }, [session, navigate]);

  // Resend cooldown countdown.
  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  async function sendCode(): Promise<void> {
    setError(null);
    setStatus('sending');
    // Start the cooldown up front so a failed resend (e.g. rate-limited) still
    // throttles the button — not only the success path.
    setResendIn(RESEND_COOLDOWN_SECONDS);
    const normalized = normalizeEmail(email);
    const { error: err } = await supabase.auth.signInWithOtp({
      email: normalized,
      options: { shouldCreateUser: true },
    });
    setStatus('idle');
    if (err) {
      setError(mapAuthError(err, 'email'));
      return;
    }
    setEmail(normalized);
    setStep('code');
  }

  async function verifyCodeWith(value: string): Promise<void> {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setError(null);
    setStatus('verifying');
    const { error: err } = await supabase.auth.verifyOtp({
      email,
      token: value,
      type: 'email',
    });
    if (err) {
      setStatus('idle');
      submittingRef.current = false;
      setError(mapAuthError(err, 'code'));
      setCode('');
      return;
    }
    // Success: keep status='verifying' and submittingRef set so the field and
    // buttons stay locked — the token is now consumed, a second verifyOtp would
    // fail. The session effect above navigates home once the session lands.
  }

  function onCodeChange(next: string): void {
    const digits = next.replace(/\D/g, '').slice(0, CODE_LENGTH);
    setCode(digits);
    // Auto-submit once the field is full; verifyCodeWith guards re-entry.
    if (digits.length === CODE_LENGTH && status !== 'verifying') {
      void verifyCodeWith(digits);
    }
  }

  function useDifferentEmail(): void {
    setStep('email');
    setCode('');
    setError(null);
  }

  return (
    <YStack
      minHeight="100vh"
      alignItems="center"
      justifyContent="center"
      padding="$4"
      backgroundColor="$background"
    >
      <YStack gap="$4" width={380} maxWidth="100%">
        {/* Brand mark on the first screen. SizableText color="$color12" recolors
            the monochrome strokes per theme (mirrors AppShell); the accent
            needle and muted " Pro" are baked into the SVG. */}
        <SizableText color="$color12" display="flex">
          <Logo variant="full" height={28} />
        </SizableText>
        <H2>Sign in to CarTrack Pro</H2>

        {step === 'email' ? (
          <>
            <Paragraph color="$color10">Enter your email. We'll send you a 6-digit code.</Paragraph>
            <Input
              placeholder="you@example.com"
              value={email}
              onChangeText={setEmail}
              autoComplete="email"
              keyboardType="email-address"
              disabled={status === 'sending'}
            />
            <Button theme="blue" onPress={sendCode} disabled={!email || status === 'sending'}>
              {status === 'sending' ? 'Sending…' : 'Send code'}
            </Button>
          </>
        ) : (
          <>
            <Paragraph color="$color10">
              Enter the 6-digit code we sent to <Paragraph fontWeight="700">{email}</Paragraph>.
            </Paragraph>
            <Input
              placeholder="123456"
              value={code}
              onChangeText={onCodeChange}
              maxLength={CODE_LENGTH}
              autoComplete="one-time-code"
              keyboardType="number-pad"
              disabled={status === 'verifying'}
            />
            <Button
              theme="blue"
              onPress={() => verifyCodeWith(code)}
              disabled={code.length !== CODE_LENGTH || status === 'verifying'}
            >
              {status === 'verifying' ? 'Verifying…' : 'Verify'}
            </Button>
            <Button chromeless onPress={sendCode} disabled={resendIn > 0 || status === 'sending'}>
              {resendIn > 0 ? `Resend code in ${resendIn}s` : 'Resend code'}
            </Button>
            <Button chromeless onPress={useDifferentEmail}>
              Use a different email
            </Button>
          </>
        )}

        {error ? <Paragraph color="$red10">{error}</Paragraph> : null}
      </YStack>
    </YStack>
  );
}
